import React from "react";
import {
  Alert,
  Box,
  Button,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import PercentRoundedIcon from "@mui/icons-material/PercentRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import { useOutletContext, useParams } from "react-router-dom";
import { erpApi } from "../../../api/erp";
import {
  ErpEmpty,
  ErpLoading,
  ErpPanel,
  ErpSectionTitle,
} from "../../../components/dashboard/erp/ErpUi";
import { ERP_BRAND, entityId } from "../../../utils/erp";
import { hasProjectPermission } from "../../../utils/projectAccess";

const emptyForm = {
  code: "",
  name: "",
  rate: "21",
  appliesTo: "both",
  calculationMode: "percentage",
  includedInPrice: false,
  salesAccountCode: "TAX_PAYABLE",
  purchaseAccountCode: "TAX_CREDIT",
  active: true,
  notes: "",
};

function profileToForm(profile = {}) {
  return {
    code: profile.code || "",
    name: profile.name || "",
    rate: String(profile.rate ?? 0),
    appliesTo: profile.appliesTo || "both",
    calculationMode: profile.calculationMode || "percentage",
    includedInPrice: profile.includedInPrice === true,
    salesAccountCode: profile.salesAccountCode || "TAX_PAYABLE",
    purchaseAccountCode: profile.purchaseAccountCode || "TAX_CREDIT",
    active: profile.active !== false,
    notes: profile.notes || "",
  };
}

export default function ProjectErpTaxesPage() {
  const { projectId } = useParams();
  const { access, bootstrap, refreshBootstrap } = useOutletContext();
  const [profiles, setProfiles] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");
  const [editing, setEditing] = React.useState(undefined);
  const [form, setForm] = React.useState(emptyForm);

  const canManage = hasProjectPermission(access, "erp.tax_profiles.manage");
  const accounts = bootstrap.accounts || [];
  const salesAccounts = accounts.filter((account) =>
    ["liability", "income"].includes(account.type),
  );
  const purchaseAccounts = accounts.filter((account) =>
    ["asset", "expense"].includes(account.type),
  );

  const load = React.useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const data = await erpApi.listTaxProfiles(projectId);
      setProfiles(data.taxProfiles || []);
    } catch (err) {
      setError(err.message || "No se pudieron cargar los impuestos");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  React.useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
  };

  const openEdit = (profile) => {
    setEditing(profile);
    setForm(profileToForm(profile));
  };

  const close = () => {
    if (saving) return;
    setEditing(undefined);
    setForm(emptyForm);
  };

  const save = async () => {
    if (!form.code.trim() || !form.name.trim()) {
      setError("Completá el código y el nombre del impuesto.");
      return;
    }
    try {
      setSaving(true);
      setError("");
      const payload = {
        ...form,
        code: form.code.trim().toUpperCase(),
        rate: Number(form.rate || 0),
        salesAccountCode: form.salesAccountCode.trim().toUpperCase(),
        purchaseAccountCode: form.purchaseAccountCode.trim().toUpperCase(),
      };
      if (editing) {
        await erpApi.updateTaxProfile(projectId, entityId(editing), payload);
      } else {
        await erpApi.createTaxProfile(projectId, payload);
      }
      setEditing(undefined);
      setForm(emptyForm);
      await Promise.all([load(), refreshBootstrap()]);
    } catch (err) {
      setError(err.message || "No se pudo guardar el impuesto");
    } finally {
      setSaving(false);
    }
  };

  if (loading && !profiles.length) {
    return <ErpLoading label="Cargando configuración impositiva..." />;
  }

  const dialogOpen = editing !== undefined;

  return (
    <Stack spacing={2.5}>
      {error ? (
        <Alert severity="error" onClose={() => setError("")}>
          {error}
        </Alert>
      ) : null}

      <ErpSectionTitle
        title="Impuestos y cargos fiscales"
        description="Perfiles reutilizables para ventas, compras y gastos. Cada movimiento conserva la tasa aplicada históricamente."
        action={
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<RefreshRoundedIcon />}
              onClick={load}
              sx={{ textTransform: "none", fontWeight: 850 }}
            >
              Actualizar
            </Button>
            {canManage ? (
              <Button
                variant="contained"
                startIcon={<AddRoundedIcon />}
                onClick={openCreate}
                sx={{ textTransform: "none", fontWeight: 850 }}
              >
                Nuevo impuesto
              </Button>
            ) : null}
          </Stack>
        }
      />

      <Alert severity="info">
        Esta configuración organiza el cálculo operativo y contable. La emisión fiscal oficial depende de la integración de facturación correspondiente.
      </Alert>

      <ErpPanel>
        {!profiles.length ? (
          <ErpEmpty
            icon={<PercentRoundedIcon />}
            title="Sin perfiles impositivos"
            description="Podés crear IVA, percepciones, tasas o cargos fijos y luego asociarlos a cada ítem."
            action={
              canManage ? (
                <Button variant="contained" onClick={openCreate}>
                  Crear perfil
                </Button>
              ) : null
            }
          />
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Impuesto</TableCell>
                  <TableCell>Aplicación</TableCell>
                  <TableCell>Cálculo</TableCell>
                  <TableCell>Incluido</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {profiles.map((profile) => (
                  <TableRow key={entityId(profile)} hover>
                    <TableCell>
                      <Typography sx={{ fontWeight: 900, color: ERP_BRAND.dark }}>
                        {profile.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>
                        {profile.code}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {profile.appliesTo === "sales"
                        ? "Ventas"
                        : profile.appliesTo === "purchases"
                          ? "Compras"
                          : "Ventas y compras"}
                    </TableCell>
                    <TableCell>
                      {profile.calculationMode === "fixed"
                        ? `$${Number(profile.rate || 0).toLocaleString("es-ES")}`
                        : `${Number(profile.rate || 0).toLocaleString("es-ES")}%`}
                    </TableCell>
                    <TableCell>{profile.includedInPrice ? "Sí" : "No"}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={profile.active ? "Activo" : "Inactivo"}
                        color={profile.active ? "success" : "default"}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {canManage ? (
                        <Button
                          size="small"
                          startIcon={<EditRoundedIcon />}
                          onClick={() => openEdit(profile)}
                          sx={{ textTransform: "none", fontWeight: 850 }}
                        >
                          Editar
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </ErpPanel>

      <Dialog open={dialogOpen} onClose={close} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontWeight: 950 }}>
          {editing ? "Editar impuesto" : "Nuevo impuesto"}
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                label="Código"
                required
                value={form.code}
                disabled={Boolean(editing)}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    code: event.target.value.toUpperCase(),
                  }))
                }
                fullWidth
              />
              <TextField
                label="Nombre"
                required
                value={form.name}
                onChange={(event) =>
                  setForm((current) => ({ ...current, name: event.target.value }))
                }
                fullWidth
              />
            </Stack>

            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                select
                label="Se aplica a"
                value={form.appliesTo}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    appliesTo: event.target.value,
                  }))
                }
                fullWidth
              >
                <MenuItem value="sales">Ventas</MenuItem>
                <MenuItem value="purchases">Compras y gastos</MenuItem>
                <MenuItem value="both">Ventas y compras</MenuItem>
              </TextField>
              <TextField
                select
                label="Modo de cálculo"
                value={form.calculationMode}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    calculationMode: event.target.value,
                  }))
                }
                fullWidth
              >
                <MenuItem value="percentage">Porcentaje</MenuItem>
                <MenuItem value="fixed">Importe fijo</MenuItem>
              </TextField>
              <TextField
                type="number"
                label={form.calculationMode === "fixed" ? "Importe" : "Tasa (%)"}
                value={form.rate}
                onChange={(event) =>
                  setForm((current) => ({ ...current, rate: event.target.value }))
                }
                inputProps={{ min: 0, step: "any" }}
                fullWidth
              />
            </Stack>

            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                select
                label="Cuenta en ventas"
                value={form.salesAccountCode}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    salesAccountCode: event.target.value,
                  }))
                }
                fullWidth
              >
                <MenuItem value="TAX_PAYABLE">Impuestos a pagar</MenuItem>
                {salesAccounts.map((account) => (
                  <MenuItem key={account.code} value={account.code}>
                    {account.code} · {account.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Cuenta en compras"
                value={form.purchaseAccountCode}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    purchaseAccountCode: event.target.value,
                  }))
                }
                fullWidth
              >
                <MenuItem value="TAX_CREDIT">Crédito fiscal</MenuItem>
                {purchaseAccounts.map((account) => (
                  <MenuItem key={account.code} value={account.code}>
                    {account.code} · {account.name}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={3}>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 900 }}>Incluido en el precio</Typography>
                <Typography variant="body2" sx={{ color: ERP_BRAND.muted }}>
                  El importe informado ya contiene este impuesto.
                </Typography>
                <Switch
                  checked={form.includedInPrice}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      includedInPrice: event.target.checked,
                    }))
                  }
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 900 }}>Perfil activo</Typography>
                <Typography variant="body2" sx={{ color: ERP_BRAND.muted }}>
                  Los perfiles inactivos se conservan para el historial.
                </Typography>
                <Switch
                  checked={form.active}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      active: event.target.checked,
                    }))
                  }
                />
              </Box>
            </Stack>

            <TextField
              label="Notas internas"
              multiline
              minRows={2}
              value={form.notes}
              onChange={(event) =>
                setForm((current) => ({ ...current, notes: event.target.value }))
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={close} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={save} disabled={saving}>
            {saving ? "Guardando..." : "Guardar impuesto"}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
